import { FC, useState, useMemo, useEffect } from "react";
import { isEmpty } from "lodash";
import { Formik, FormikHelpers } from "formik";
import {
  FormikSelectField,
  FormikInputField,
  Modal,
  toast,
  types
} from "@vilocnv/allsetra-core";
import { ModalProps, Stack, useTheme, Box, Typography } from "@mui/material";

// Data
import { IRideBulkUpdateDriverOrKey } from "app/data/types";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  getRidesBulkUpdateDriverOrKeyInitialValues,
  ridesBulkUpdateDriverOrKeyValidationSchema,
  getUserRole
} from "app/data/helpers";
import {
  selectDrawerSelectedAccountId,
  selectUsersState,
  selectAccountKeysState,
  selectAccountsState,
  selectSettingsState,
  selectUserRolesState
} from "app/data/selectors";
import {
  getAllUsersThunk,
  getAllKeysByAccountThunk,
  getAllUserRolesThunk,
  getSpecificAccountUserThunk,
  getSpecificSettingThunk,
  updateRideBulkUploadDriverThunk,
  updateRideBulkUploadKeysThunk
} from "app/features";
import { useTranslation } from "react-i18next";
import { SignalRService } from "app/data/services";

type Props = Omit<ModalProps, "title" | "children"> & {
  rides: string[];
  afterSubmitActionHandler: (rideIds?: string[]) => void;
};

const ALLOWED_ROLES = ["Super Admin", "Admin", "Manager"];

const BulkRidesUpdateDriverForm: FC<Props> = ({
  open,
  onClose,
  rides,
  afterSubmitActionHandler
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [customIsSubmitting, setCustomIsSubmitting] = useState<boolean>(false);

  // Global State
  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { allUsers, loading: usersLoading } = useAppSelector(selectUsersState);
  const { accountKeys, loading: accountKeysLoading } = useAppSelector(
    selectAccountKeysState
  );
  const { accountUser } = useAppSelector(selectAccountsState);
  const { specificSetting } = useAppSelector(selectSettingsState);
  const { userRoles } = useAppSelector(selectUserRolesState);

  // @ts-ignore
  const accountRole = getUserRole(accountUser?.role, userRoles);

  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  useEffect(() => {
    dispatch(getAllUsersThunk(accountId ?? ""));
    dispatch(getAllKeysByAccountThunk(accountId ?? ""));
    dispatch(getSpecificSettingThunk());
    dispatch(getAllUserRolesThunk());
  }, [accountId]);

  useEffect(() => {
    if (accountId !== null && (specificSetting as any)?.uniqueId) {
      dispatch(
        getSpecificAccountUserThunk({
          accountId: accountId,
          userId: (specificSetting as any).uniqueId
        })
      );
    }
  }, [accountId, (specificSetting as any)?.uniqueId]);

  const initialValues = useMemo(
    () =>
      !isEmpty(rides) ? getRidesBulkUpdateDriverOrKeyInitialValues(rides) : {},
    [rides]
  );

  const onFulfilledRequest = (
    formikHelpers: FormikHelpers<IRideBulkUpdateDriverOrKey>,
    rideIds: string[]
  ) => {
    formikHelpers.setSubmitting(false);
    setCustomIsSubmitting(false);

    toast.success("Bulk update has been completed.");

    if (afterSubmitActionHandler) afterSubmitActionHandler(rideIds);

    // @ts-ignore
    onClose();
  };

  const saveChangesHandler = async (
    values: IRideBulkUpdateDriverOrKey,
    formikHelpers: FormikHelpers<IRideBulkUpdateDriverOrKey>
  ) => {
    formikHelpers.setSubmitting(true);
    setCustomIsSubmitting(true);

    let expectedType = "";
    let expectedEventName: any = null;

    if (values.driverId) {
      const { type } = await dispatch(
        updateRideBulkUploadDriverThunk({
          accountId: accountId ?? "",
          data: {
            rides: values.rides,
            driverId: values.driverId,
            changeReason: values.changeReason
          }
        })
      );
      expectedType = "objects/updateRideBulkUploadDriverThunk/fulfilled";
      expectedEventName =
        (types.BackendEventsEnum as any).RideDriverBulkUpdatedEvent ||
        types.BackendEventsEnum.RideUpdatedEvent;

      if (type !== expectedType) {
        formikHelpers.setSubmitting(false);
        setCustomIsSubmitting(false);
        return;
      }
    } else if (values.keyId) {
      const { type } = await dispatch(
        updateRideBulkUploadKeysThunk({
          accountId: accountId ?? "",
          data: {
            rides: values.rides,
            keyId: values.keyId,
            changeReason: values.changeReason
          }
        })
      );
      expectedType = "objects/updateRideBulkUploadKeysThunk/fulfilled";
      expectedEventName = types.BackendEventsEnum.RideKeyBulkUpdatedEvent;

      if (type !== expectedType) {
        formikHelpers.setSubmitting(false);
        setCustomIsSubmitting(false);
        return;
      }
    } else {
      formikHelpers.setSubmitting(false);
      setCustomIsSubmitting(false);
      return;
    }

    if (expectedType) {
      // Set a timeout to stop listening after 10 seconds
      const timeoutId = setTimeout(() => {
        SignalRService.hubConnection?.off("EventRaised");
        onFulfilledRequest(formikHelpers, values.rides);
        console.log("Stopped listening for EventRaised after 10 seconds.");
      }, 10000);

      const handleEventRaised = (event: any) => {
        if (event.eventName === expectedEventName) {
          onFulfilledRequest(formikHelpers, values.rides);

          // Clear the timeout if the event is received
          clearTimeout(timeoutId);

          // Remove the listener if it's a one-time use
          SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
        }
      };

      SignalRService.hubConnection?.on("EventRaised", handleEventRaised);
    }
  };

  return (
    <Formik
      // @ts-ignore
      initialValues={initialValues}
      validationSchema={ridesBulkUpdateDriverOrKeyValidationSchema(t)}
      onSubmit={saveChangesHandler}
      enableReinitialize
      validateOnMount
    >
      {({
        resetForm,
        isValid,
        isSubmitting,
        handleSubmit,
        setFieldValue,
        values
      }) => (
        <Modal
          open={open}
          onClose={() => {
            // @ts-ignore
            onClose();
            resetForm();
          }}
          title={t("common.changeDriver")}
          primaryBtnProps={{
            type: "submit",
            text: t("buttonsText.saveChanges"),
            loading: isSubmitting || customIsSubmitting,
            disabled: !isValid,
            // @ts-ignore
            onClick: handleSubmit
          }}
          secondaryBtnProps={{
            text: t("buttonsText.cancel"),
            onClick: () => {
              // @ts-ignore
              onClose();
              resetForm();
            }
          }}
          theme={theme}
        >
          <Stack spacing={2}>
            <FormikSelectField
              key={`keyId-${values.driverId || "empty"}`}
              theme={theme}
              label={t("keyId.label", { ns: "formFieldsTranslation" })}
              placeholder={t("keyId.placeholder", {
                ns: "formFieldsTranslation"
              })}
              name="keyId"
              options={accountKeys ?? []}
              optionLabelKey={"label"}
              optionValueKey={"uniqueId"}
              loading={accountKeysLoading}
              searchable
              disabled={!ALLOWED_ROLES.includes(accountRole)}
              onChange={() => {
                setFieldValue("driverId", null);
              }}
              onClearText={() => {
                setFieldValue("keyId", null);
              }}
            />
            <Box sx={{ textAlign: "center", color: "text.secondary" }}>
              {t("or", { ns: "formFieldsTranslation" })}
            </Box>
            <FormikSelectField
              key={`driverId-${values.keyId || "empty"}`}
              theme={theme}
              label={t("driver.label", { ns: "formFieldsTranslation" })}
              placeholder={t("driver.placeholder", {
                ns: "formFieldsTranslation"
              })}
              name="driverId"
              options={allUsers ?? []}
              optionLabelKey={"email"}
              optionValueKey={"uniqueId"}
              loading={usersLoading}
              searchable
              disabled={!ALLOWED_ROLES.includes(accountRole)}
              onChange={() => {
                setFieldValue("keyId", null);
              }}
              onClearText={() => {
                setFieldValue("driverId", null);
              }}
            />
            {!ALLOWED_ROLES.includes(accountRole) && (
              <Typography
                variant="body2"
                color="error"
                sx={{ mt: 1, fontSize: "0.875rem" }}
              >
                {t("errors.insufficientPermissions", {
                  ns: "formFieldsTranslation",
                  defaultValue:
                    "You don't have permission to assign drivers. Only Admin, and Manager roles can assign drivers."
                })}
              </Typography>
            )}
            <FormikInputField
              theme={theme}
              label={t("changeReason.label", { ns: "formFieldsTranslation" })}
              required
              name="changeReason"
              fullWidth
              placeholder={t("changeReason.placeholder", {
                ns: "formFieldsTranslation"
              })}
            />
          </Stack>
        </Modal>
      )}
    </Formik>
  );
};

export default BulkRidesUpdateDriverForm;
